import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useApiClient } from '../../../helpers/apiClient.helper';
import { useSupabaseAuth } from '../../shared/contexts/SupabaseAuthContext';
import { fetchXpSummary, type XpSummary } from '../helpers/xpApi';

export const XP_SUMMARY_QUERY_KEY = ['xpSummary'] as const;

/**
 * Own-XP summary (specs/xp-and-ranks). Level/rank arrive derived from the server ledger.
 * Fresh-enough for the header; call `invalidate` after actions that just earned XP
 * (daily claim, game completion) to celebrate without waiting for staleness.
 */
export function useXpSummary() {
  const { isSignedIn, isLoaded, user } = useSupabaseAuth();
  const apiClient = useApiClient();

  return {
    ...useQuery<XpSummary>({
      queryKey: XP_SUMMARY_QUERY_KEY,
      queryFn: () => fetchXpSummary(apiClient),
      enabled: isLoaded && isSignedIn === true,
      staleTime: 60 * 1000,
    }),
    userId: user?.id ?? null,
  };
}

/** Invalidate the XP summary after an action that just earned XP. */
export function useInvalidateXpSummary() {
  const queryClient = useQueryClient();
  return useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: XP_SUMMARY_QUERY_KEY });
  }, [queryClient]);
}
